// Bound waiting input, not transport time: a dispatched command is never retried
// or assumed cancelled just because its browser scope has gone away.
export const ADB_INPUT_MAX_PENDING = 12;
export const ADB_INPUT_MAX_AGE_MS = 5000;
export const ADB_REPEAT_MAX_AGE_MS = 300;
export const ADB_INPUT_MAX_BATCH = 10;

export function createAdbRemoteInputQueue({ now = Date.now } = {}) {
  let pending = [];
  let running = false;
  let generation = 0;
  let active = [];
  const stoppedHolds = new WeakSet();
  const stopHold = (holdId) => {
    if (holdId && typeof holdId === 'object') stoppedHolds.add(holdId);
  };
  const discard = (entry, reason) => {
    entry.onDiscard?.(reason);
    entry.resolve({ status: reason });
  };
  const remove = (predicate, reason) => {
    const removed = pending.filter(predicate);
    pending = pending.filter((entry) => !predicate(entry));
    removed.forEach((entry) => discard(entry, reason));
  };
  const clear = (reason = 'cancelled') => {
    generation += 1;
    active.forEach((entry) => stopHold(entry.holdId));
    pending.forEach((entry) => stopHold(entry.holdId));
    remove(() => true, reason);
  };
  const drain = async () => {
    if (running) return;
    running = true;
    try {
      while (pending.length) {
        const entry = pending.shift();
        if (!entry.isCurrent()) {
          discard(entry, 'cancelled');
          continue;
        }
        if (now() - entry.createdAt >= entry.maxAgeMs) {
          stopHold(entry.holdId);
          discard(entry, 'expired');
          // Continuing an ordered digit sequence after losing a digit is unsafe.
          clear('cancelled');
          break;
        }
        const batch = [entry];
        // Batch only explicitly opted-in, adjacent discrete taps. Held repeats
        // must remain cancellable on release and never become an in-flight batch.
        while (entry.batchKey && !entry.repeat && batch.length < ADB_INPUT_MAX_BATCH) {
          const next = pending[0];
          if (!next || next.repeat || next.batchKey !== entry.batchKey
              || next.generation !== entry.generation || !next.isCurrent()
              || now() - next.createdAt >= next.maxAgeMs) break;
          batch.push(pending.shift());
        }
        let result;
        active = batch;
        try {
          result = await entry.send(batch.length);
        } catch {
          result = false;
        }
        batch.forEach((item) => item.resolve({ status: result === false ? 'failed' : 'sent' }));
        if (result === false && entry.generation === generation) clear('cancelled');
        active = [];
      }
    } finally {
      running = false;
    }
  };
  return {
    enqueue({ send, isCurrent, onDiscard, holdId = null, repeat = false, batchKey = null }) {
      if (repeat && holdId && stoppedHolds.has(holdId)) return Promise.resolve({ status: 'released' });
      // Only one unsent repeat per hold; discrete taps retain their own entries.
      const waitingRepeat = repeat && pending.find((entry) => entry.repeat && entry.holdId === holdId);
      if (waitingRepeat) {
        // A continuing physical hold is fresh intent, rather than an old backlog.
        waitingRepeat.createdAt = now();
        return Promise.resolve({ status: 'coalesced' });
      }
      if (pending.length >= ADB_INPUT_MAX_PENDING) {
        // Stop the remaining sequence instead of dropping a middle digit.
        clear('overflow');
        onDiscard?.('overflow');
        return Promise.resolve({ status: 'overflow' });
      }
      return new Promise((resolve) => {
        pending.push({ send, isCurrent, onDiscard, holdId, repeat, batchKey, resolve,
          createdAt: now(), generation,
          maxAgeMs: repeat ? ADB_REPEAT_MAX_AGE_MS : ADB_INPUT_MAX_AGE_MS });
        void drain();
      });
    },
    release(holdId) {
      stopHold(holdId);
      remove((entry) => entry.repeat && entry.holdId === holdId, 'released');
    },
    clear,
  };
}
